/**
 * Help center registry.
 *
 * Articles live as plain JS modules — each default-exports
 * { slug, title, category, summary, related, Body }. The registry imports
 * them eagerly and groups by category for the side nav and the search index.
 *
 * Why no MDX / CMS:
 *  - articles are small enough that prose-in-JSX is fine
 *  - keeping content in the repo makes it reviewable in PRs
 *  - no build-step dependency creeps in (CRA setup stays untouched)
 *  - search is just a substring match over title+summary+plain-text body
 */
import gettingStarted from './articles/getting-started';
import addingClients from './articles/adding-clients';
import managingPipeline from './articles/managing-pipeline';
import creatingDeals from './articles/creating-deals';
import foldersIntro from './articles/folders-intro';
import folderLifecycle from './articles/folder-lifecycle';
import healthScore from './articles/health-score';
import compliance from './articles/compliance';
import aiEmail from './articles/ai-email';
import sendingForSignature from './articles/sending-for-signature';
import brokerDashboard from './articles/broker-dashboard';
import teamWorkspace from './articles/team-workspace';
import integrationsOverview from './articles/integrations-overview';
import apiKeys from './articles/api-keys';
import webhooks from './articles/webhooks';
import publicApi from './articles/public-api';
import branding from './articles/branding';
import dataRights from './articles/data-rights';

export const CATEGORIES = [
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'leads-deals', label: 'Leads & Deals' },
  { id: 'folders', label: 'Folders & E-Signing' },
  { id: 'ai', label: 'AI Tools' },
  { id: 'team', label: 'Teams & Brokerages' },
  { id: 'integrations', label: 'Integrations & API' },
  { id: 'account', label: 'Account & Compliance' },
];

export const ARTICLES = [
  gettingStarted,
  addingClients,
  managingPipeline,
  creatingDeals,
  foldersIntro,
  folderLifecycle,
  sendingForSignature,
  healthScore,
  compliance,
  aiEmail,
  brokerDashboard,
  teamWorkspace,
  integrationsOverview,
  apiKeys,
  webhooks,
  publicApi,
  branding,
  dataRights,
];

// Map for quick slug lookup
export const ARTICLE_BY_SLUG = ARTICLES.reduce((acc, a) => {
  acc[a.slug] = a;
  return acc;
}, {});

// Group articles by category — same order as CATEGORIES.
export function articlesByCategory() {
  return CATEGORIES.map((c) => ({
    ...c,
    articles: ARTICLES.filter((a) => a.category === c.id),
  })).filter((c) => c.articles.length > 0);
}

/**
 * Plain-text search index for the article list.
 * Body components aren't pre-rendered (would require ReactDOMServer), so we
 * index title + summary + the optional `searchTerms` field on each article
 * — enough for "find the article about X" without dragging in heavy infra.
 */
function buildIndexEntry(a) {
  return [a.title, a.summary, ...(a.searchTerms || [])].join(' ').toLowerCase();
}

export function searchArticles(query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return ARTICLES
    .map((a) => ({ article: a, hay: buildIndexEntry(a) }))
    .filter(({ hay }) => hay.includes(q))
    .map(({ article }) => article);
}
